'use client';

import clsx from 'clsx';
import { useContext, type ForwardedRef, type ReactNode } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { IconMenuMoreVertical } from '../../icons/index.js';
import { useIsMobile } from '../../utils/use-is-mobile.js';
import { AppShellContext } from '../app-shell/app-shell-context.js';
import { IconButton } from '../button/icon-button.js';
import { Divider } from '../divider/divider.js';
import { Popover, PopoverRoot } from '../popover/popover.js';
import type { CommonProps, Href, NavigationProps, WithRequired } from '../types.js';
import { Link } from '../typography/link.js';
import { Text } from '../typography/text.js';
import { CimpressLogo } from './cimpress-logo.js';
import { UNSTABLE_AppHeaderNavButton as AppHeaderNavButton } from './nav-button.js';

export interface AppHeaderTitleLinkProps extends WithRequired<NavigationProps, 'href'> {}

export interface AppHeaderProps extends CommonProps {
  /** The title displayed in the header, usually the application name. Rendered as an `<h1>` element. */
  title: string;
  /** The props of the link that is rendered around the header title. */
  titleLink: Href | AppHeaderTitleLinkProps;
  /** The tools to render at the end of the header. */
  tools?: ReactNode;
  UNSTABLE_navButton?: ReactNode;
  UNSTABLE_postTitle?: ReactNode;
  UNSTABLE_search?: ReactNode;
}

function AppHeader(
  {
    title,
    titleLink,
    tools,
    UNSTABLE_navButton: navButton,
    UNSTABLE_postTitle: postTitle,
    UNSTABLE_search: search,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: AppHeaderProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const isMobile = useIsMobile();
  const appShellContext = useContext(AppShellContext);
  const titleLinkProps = typeof titleLink === 'object' && 'href' in titleLink ? titleLink : { href: titleLink };

  return (
    <header
      {...props}
      ref={ref}
      className={clsx('cim-app-header', UNSAFE_className)}
      style={UNSAFE_style}
      data-react-aria-top-layer={appShellContext?.sideNavIsOpen || undefined}
    >
      <div className="cim-app-header-title">
        {navButton !== undefined ? navButton : appShellContext ? <AppHeaderNavButton /> : null}
        <CimpressLogo height={24} />
        <Divider orientation="vertical" />
        <Text as="h1" variant="title-5" tone="base">
          <Link {...titleLinkProps} UNSAFE_className="cim-app-header-title-link">
            {title}
          </Link>
        </Text>

        {postTitle}
      </div>

      {search && <div className="cim-app-header-search">{search}</div>}

      <div className="cim-app-header-tools">
        {isMobile && tools ? (
          <PopoverRoot>
            <IconButton
              icon={<IconMenuMoreVertical />}
              // TODO: i18n
              aria-label="Expand/collapse header"
              variant="tertiary"
              size="small"
            />
            <Popover aria-label="Expanded header" placement="bottom end">
              <div className="cim-app-header-tools-collapsed">{tools}</div>
            </Popover>
          </PopoverRoot>
        ) : (
          tools
        )}
      </div>
    </header>
  );
}

/**
 * Displays a basic application header.
 *
 * See [app header usage guidelines](https://ui.cimpress.io/components/app-header/).
 */
const _AppHeader = forwardRef(AppHeader, 'AppHeader');

export { _AppHeader as AppHeader };
